import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/apiFetch';

/**
 * GlowyaiSunum: GlowyAI'ın sohbette hazırladığı sunumu gösterir.
 * Route: /glowyai/sunum/:id?b=<brandId>  (sidebar YOK, tam ekran deck)
 *
 * Sunum backend'de slayt planı olarak saklanıyor; HTML her açılışta
 * GET /api/ai/presentations/:id ile üretilip iframe srcDoc içine basılır
 * (belgenin global CSS'i panelin stilini bozmasın diye izole). PDF aynı
 * uçtan /pdf ile sunucuda üretilip indirilir.
 *
 * Marka linkteki ?b= parametresinden gelir; yoksa seçili marka kullanılır.
 * Böylece kullanıcı sohbetten sonra marka değiştirse de link doğru sunumu açar.
 */
const API = 'https://api.glowytics.com';

function authHeaders(brandId) {
  const headers = { 'X-Brand-ID': brandId || '' };
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export default function GlowyaiSunum() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const brandId = params.get('b') || localStorage.getItem('currentBrandId');
  const [deck, setDeck] = useState(null);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch(`${API}/api/ai/presentations/${encodeURIComponent(id)}`, { headers: authHeaders(brandId) })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!alive) return;
        if (!r.ok || !j.success) setError(j.error || 'Sunum yüklenemedi.');
        else setDeck({ ...j, title: String(j.title || 'Sunum').replace(/\s+/g, ' ').trim() });
      })
      .catch(() => { if (alive) setError('Sunum yüklenemedi.'); });
    return () => { alive = false; };
  }, [id, brandId]);

  useEffect(() => {
    if (deck && deck.title) document.title = `${deck.title} · GlowyAI`;
  }, [deck]);

  const downloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await apiFetch(`${API}/api/ai/presentations/${encodeURIComponent(id)}/pdf`, { headers: authHeaders(brandId) });
      if (!r.ok) throw new Error('pdf');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(deck && deck.title) || 'sunum'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (_) {
      alert('PDF üretilemedi, birazdan tekrar dener misin?');
    } finally {
      setPdfBusy(false);
    }
  };

  const shell = { position: 'fixed', inset: 0, background: '#f0f0f2', display: 'flex', flexDirection: 'column' };

  if (error) {
    return (
      <div style={{ ...shell, alignItems: 'center', justifyContent: 'center', font: '15px -apple-system, sans-serif', color: '#6e6e73' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={shell}>
      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e0e0e3', font: '14px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 600, color: '#1d1d1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {deck ? deck.title : 'Sunum yükleniyor…'}
          {deck && <span style={{ fontWeight: 500, color: '#86868b', marginLeft: 8 }}>{deck.slideCount} slayt</span>}
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={!deck || pdfBusy}
          style={{ font: 'inherit', fontWeight: 500, color: '#fff', background: '#1d1d1f', border: 0, borderRadius: 100, padding: '8px 18px', cursor: deck && !pdfBusy ? 'pointer' : 'default', opacity: deck && !pdfBusy ? 1 : 0.5 }}
        >
          {pdfBusy ? 'PDF hazırlanıyor…' : 'PDF olarak indir'}
        </button>
      </div>
      {deck && <iframe title={deck.title} srcDoc={deck.html} style={{ flex: '1 1 auto', width: '100%', border: 0 }} />}
    </div>
  );
}
